import { Tracking } from './Tracking.interfaces';

/**
 * Interface for the Com_Parcels table.
 * Represents complementary parcel data associated with companies and dealers.
 */
export interface ComParcel {
  /** Unique ID for complementary data */
  parcelComplementaryId?: number;

  /** Reference company code */
  parcelComplementaryCode?: string;

  /** Client brand code associated with the parcel */
  parcelComplementaryBrandCode?: string;

  /** Complementary parameter name */
  parcelComplementaryName?: string;

  /** Category or type of complementary data */
  parcelComplementaryType?: string;

  /** Numeric value of the complementary data */
  parcelComplementaryValueNumber?: number;

  /** Text value of the complementary data */
  parcelComplementaryValueText?: string;

  /** Date value of the complementary data */
  parcelComplementaryValueDate?: string;

  /** CLOB value of the complementary data */
  parcelComplementaryValueClob?: string;

  /** Status of the complementary data */
  parcelComplementaryStatus?: string;

  /** Creation date of the complementary data */
  parcelComplementaryCreatedAt?: string;

  /** Waybill code */
  parcelComplementaryWaybillCode?: string;

  /** Parcel ID */
  parcelId?: number;

  /** Original latitude */
  parcelComplementaryOriginalLatitude?: number;

  /** Original longitude */
  parcelComplementaryOriginalLongitude?: number;

  /** Warehouse code */
  warehouseCode?: string;

  /** Dealer code */
  dealerCode?: string;

  /** Event type */
  parcelComplementaryEventType?: string;

  /** Event status */
  parcelComplementaryEventStatus?: string;

  /** Carrier code */
  carrierCode?: string;

  /** External trip code */
  externalTripCode?: string;

  /** Warehouse from */
  parcelComplementaryWarehouseFrom?: string;

  /** Warehouse to */
  parcelComplementaryWarehouseTo?: string;

  /** Dealer from */
  parcelComplementaryDealerFrom?: string;

  /** Dealer to */
  parcelComplementaryDealerTo?: string;

  /** Event start date */
  parcelComplementaryEventStart?: string;

  /** Event end date */
  parcelComplementaryEventEnd?: string;

  /** Complementary carrier code */
  parcelComplementaryCarrierCode?: string;

  /** Vettore della loading list della tappa (valorizzato dal backend in /com-parcels e /damaged-parcels/{id}/routing) */
  loadingListCarrierCode?: string;

  /** Vettore del gruppo di default del tour routing della tappa (come sopra) */
  carrierCodeDefaultGroup?: string;

  /** Justification ID */
  parcelComplementaryJustificationId?: number;

  /** Justification comment */
  parcelComplementaryJustificationComment?: string;

  /** Sequence number */
  parcelComplementarySequence?: number;

  /** Tour routing ID */
  parcelComplementaryTourRoutingId?: number;

  /** Updated at timestamp */
  parcelComplementaryUpdatedAt?: string;

  /** Updated by user */
  parcelComplementaryUpdatedBy?: string;

  /** Event start by user */
  parcelComplementaryEventStartBy?: string;

  /** Event end by user */
  parcelComplementaryEventEndBy?: string;

  /** Is parcel complementary leg scan mandatory */
  isParcelComplementaryLegScanMandatory?: boolean;

  parcelComplementaryDepPointId?: string;

  parcelComplementaryArrPointId?: string;

  parcelComplementaryTripId?: string;
}

/**
 * Transcodifica degli stati di una tappa di instradamento (parcelComplementaryStatus)
 * nella relativa etichetta italiana. La chiave vuota vale per le tappe senza stato.
 */
export const COM_PARCEL_STATUS_LABELS: { [status: string]: string } = {
  IN_PROGRESS: 'In corso',
  PLANNED: 'Pianificato',
  EXECUTED: 'Eseguito',
  UNKNOWN: 'Sconosciuto',
  MISROUTING: 'Misrouting',
  '': 'Non definito'
};

/**
 * Interface for the Com_Warehouses table.
 * Represents warehouse data with complete address and contact information.
 */
export interface ComWarehouse {
  /** Unique ID for the warehouse */
  warehouseId?: number;

  /** Warehouse code (unique) */
  warehouseCode: string;

  /** Warehouse name */
  warehouseName?: string;

  /** Warehouse short name */
  warehouseShortName?: string;

  /** Warehouse location ID */
  warehouseLocationId?: string;

  /** Warehouse address street */
  warehouseAddressStreet?: string;

  /** Warehouse address number */
  warehouseAddressNumber?: string;

  /** Warehouse address ZIP code */
  warehouseAddressZip?: string;

  /** Warehouse address city */
  warehouseAddressCity?: string;

  /** Warehouse address province */
  warehouseAddressProvince?: string;

  /** Warehouse address country */
  warehouseAddressCountry?: string;

  /** Warehouse capacity */
  warehouseCapacity?: number;

  /** Warehouse contact person */
  warehouseContactPerson?: string;

  /** Warehouse contact phone */
  warehouseContactPhone?: string;

  /** Warehouse latitude */
  warehouseLatitude?: number;

  /** Warehouse longitude */
  warehouseLongitude?: number;

  /** Warehouse type */
  warehouseType?: string;

  /** Warehouse status (default: ACTIVE) */
  warehouseStatus?: string;

  /** Warehouse version */
  warehouseVersion?: number;

  /** Warehouse affiliate code */
  warehouseAffiliateCode?: string;

  /** Warehouse affiliate type */
  warehouseAffiliateType?: string;

  /** Updated at timestamp */
  warehouseUpdatedAt?: string;

  /** Updated by user */
  warehouseUpdatedBy?: string;

  /** Creation date of the warehouse */
  warehouseCreatedAt?: string;

  /** Warehouse external client code */
  warehouseExternalClientCode?: string;
}

/**
 * Interface for the Com_Parcels entity.
 * Extends ComParcel with additional fields from the entity class.
 */
export interface ComParcels extends ComParcel {
  /** Loading list ID */
  parcelComplementaryLoadingListId?: number;

  /** Barcode of the complementary parcel */
  parcelComplementaryBarcode?: string;
}

/**
 * Interface for the response of ComParcels endpoint.
 * Contains both ComParcels list and ComWarehouses list for mapping warehouse codes to names.
 */
export interface ComParcelResponse {
  /** List of complementary parcels */
  comParcels: ComParcel[];

  /** List of warehouses for mapping codes to names */
  comWarehouses: ComWarehouse[];

  /** Picking events of the parcel (Opr_Trackings with operationType PICKING), oldest first */
  pickingEvents?: Tracking[];
}